/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { generateUrl } from '../../routes/util';
import G17Client from '../../services/G17Client';
import {
  CustomReportMetric,
  CustomReportProps,
  CustomReportsValue,
  getMetricTypeLabel,
  MetricType,
} from '../../types/custom-report';
import { CustomReportRoutes } from './CustomReportRoutes';
import IconButton from '@g17eco/molecules/button/IconButton';
import CustomReportsMetricDetailsModal from './CustomReportsMetricDetailsModal';
import { UniversalTrackerValueView } from '@features/universal-tracker-value';
import './styles.scss';
import { useRemoveMetricFromCustomReportMutation } from '@api/custom-reports';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { ColumnDef, Table } from '@g17eco/molecules/table';

interface CustomReportMetricTableData extends CustomReportMetric {
  name: string;
  metricType: string;
  value: JSX.Element;
  buttons: {
    detailsClick: (() => void) | undefined;
    deleteClick: () => Promise<void>;
    isCalculation: boolean;
  };
}

const getFormattedValue = (reportValue?: CustomReportsValue) => {
  if (!reportValue) {
    return <>-</>;
  }
  if (reportValue.customMetricType === MetricType.Text) {
    return (
      <SimpleTooltip text='Can only be viewed in file download'>
        <i className='fa fa-info-circle' />
      </SimpleTooltip>
    );
  }
  return (
    <UniversalTrackerValueView
      utr={reportValue.utr}
      utrv={reportValue.utrv}
      valueType={reportValue.utr.valueType}
    />
  );
};

const columns: ColumnDef<CustomReportMetricTableData>[] = [
  {
    accessorKey: 'name',
    header: 'Metric name',
  },
  {
    accessorKey: 'metricType',
    header: 'Type',
  },
  {
    accessorKey: 'value',
    header: 'Result',
    cell: ({ row }) => row.original.value,
  },
  {
    id: 'buttons',
    header: '',
    meta: {
      cellProps: {
        className: 'text-center',
      },
      style: {
        minWidth: 150,
      },
    },
    cell: ({ row }) => {
      const { buttons } = row.original;
      return (
        <div className='text-right'>
          <div className='d-inline mr-2 border-end' />
          <IconButton
            icon='fa-filter'
            disabled={!buttons.isCalculation}
            tooltip='View disaggregations'
            key={'op-details'}
            className={'mr-1'}
            onClick={buttons.detailsClick}
          />
          <IconButton
            colour={5}
            icon='fa-trash'
            tooltip='Delete metric'
            key={'op-delete'}
            className={'mr-1'}
            onClick={buttons.deleteClick}
          />
        </div>
      );
    },
  },
];

interface UtrvState {
  loaded: boolean;
  values: CustomReportsValue[];
}

const CustomReportMetricsList = ({ customReport, initiativeId }: CustomReportProps) => {

  const [metricsList, setMetricsList] = useState<CustomReportMetric[]>([]);
  const [showDetails, setShowDetails] = useState<string | undefined>(undefined);

  const [utrvState, setUtrvState] = useState<UtrvState>({ loaded: false, values: [] });

  React.useEffect(() => {
    if (initiativeId && customReport?._id) {
      G17Client.getCustomReportValues(initiativeId, customReport?._id)
        .then((values) => setUtrvState({ loaded: true, values: values }))
        .catch(() => setUtrvState({ loaded: true, values: [] }));
    }
  }, [initiativeId, customReport]);

  const history = useHistory();

  React.useEffect(() => {
    if (customReport?.metrics) {
      setMetricsList(customReport.metrics);
    }
  }, [customReport]);

  const [removeMetricFromCustomReport, { isLoading }] = useRemoveMetricFromCustomReportMutation();

  const handleDelete = useCallback(
    async (id: string) => {
      if (!customReport) {
        return;
      }

      removeMetricFromCustomReport({ initiativeId, reportId: customReport._id, metricId: id })
        .unwrap()
        .then(() => {
          history.push(generateUrl(CustomReportRoutes.MANAGE_REPORT, { initiativeId, reportId: customReport._id }));
        });
    },
    [customReport, history, initiativeId, removeMetricFromCustomReport]
  );

  const handleGoToAdd = () => {
    if (!customReport) {
      return;
    }
    const url = generateUrl(CustomReportRoutes.ADD_METRIC, {
      initiativeId: customReport.initiativeId,
      reportId: customReport._id,
    });
    history.push(url);
  };

  const handleToggleDetails = (utrId?: string) => {
    setShowDetails((currentUtrId) => (currentUtrId === utrId ? undefined : utrId));
  };

  const data: CustomReportMetricTableData[] = useMemo(
    () =>
      !customReport
        ? []
        : metricsList.map((metric) => {
            const value = utrvState.values.find(({ utr }) => utr._id === metric.utrId);
            const isCalculation = metric.type === MetricType.Calculated;
            return {
              ...metric,
              name: customReport.universalTrackers?.find((utr) => utr._id === metric.utrId)?.name ?? '',
              metricType: getMetricTypeLabel(metric.type),
              value: <span className='pr-3'>{getFormattedValue(value)}</span>,
              buttons: {
                detailsClick: isCalculation ? () => handleToggleDetails(metric.utrId) : undefined,
                deleteClick: () => handleDelete(metric.utrId),
                isCalculation,
              },
            };
          }),
    [customReport, handleDelete, metricsList, utrvState.values]
  );

  return (
    <>
      <Table columns={columns} data={data} noData='Use the button below to add metrics to this custom report' />
      <div className='text-center mt-4'>
        <Button disabled={isLoading || !customReport} outline={true} onClick={handleGoToAdd}>
          Add a new metric
        </Button>
      </div>
      {utrvState.loaded && showDetails && customReport ? (
        <CustomReportsMetricDetailsModal
          customReport={customReport}
          metricValues={utrvState.values}
          utrId={showDetails}
          toggle={handleToggleDetails}
        />
      ) : (
        <></>
      )}
    </>
  );
};

export default CustomReportMetricsList;
